import { entryKind, type HistoryEntry } from "@patients/historyEntries";
import {
  Button,
  EmptyNote,
  HistoryItem,
  HistoryRail,
} from "@scribenote/brian-design-system";

const yearFormat = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** HistoryItem's own label drops the year, so older items carry theirs. */
const dateLabel = (iso: string): string | undefined => {
  const at = new Date(iso);
  return at.getFullYear() === new Date().getFullYear()
    ? undefined
    : yearFormat.format(at).replace(",", "");
};

const entryMeta = (
  entry: HistoryEntry,
  roomName: (id: string | null) => string | null,
): string | null => {
  if (entry.type === "view") return entry.view.subtitle;
  const { visit } = entry;
  return (
    [
      visit.reason ? visit.visitType : null,
      visit.assignedUserName,
      roomName(visit.currentRoomUuid),
      visit.cancelledAt ? "cancelled" : null,
    ]
      .filter(Boolean)
      .join(" · ") || null
  );
};

const entryTitle = (entry: HistoryEntry): string =>
  entry.type === "visit"
    ? (entry.visit.reason ?? entry.visit.visitType)
    : entry.view.name;

export const PatientHistory = ({
  entries,
  selectedId,
  onSelect,
  roomName,
  complete,
  loadingMore,
  onLoadMore,
}: {
  entries: HistoryEntry[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  roomName: (id: string | null) => string | null;
  /** Every visit is loaded. */
  complete: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) => {
  const oldest = entries.at(-1);
  const summary = [
    `${entries.length}${complete ? "" : "+"} ${entries.length === 1 ? "item" : "items"}`,
    complete && oldest ? `since ${new Date(oldest.at).getFullYear()}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <div className="flex shrink-0 items-baseline justify-between border-b-[1.5px] border-ink pb-2.5">
        <h2 className="font-display text-xl font-medium text-ink">History</h2>
        {entries.length > 0 && (
          <span className="text-mono-time text-fine-print text-annotation">
            {summary}
          </span>
        )}
      </div>
      <div className="-mx-2 min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {entries.length === 0 ? (
          <EmptyNote>Nothing on file yet.</EmptyNote>
        ) : (
          <HistoryRail aria-label="History" className="mt-3.5 ml-2">
            {entries.map((entry) => (
              <HistoryItem
                key={entry.id}
                kind={entryKind(entry)}
                date={entry.at}
                dateLabel={dateLabel(entry.at)}
                title={entryTitle(entry)}
                selected={entry.id === selectedId}
                onSelect={() => onSelect(entry.id)}
                data-dd-privacy="hidden"
              >
                {entryMeta(entry, roomName)}
              </HistoryItem>
            ))}
          </HistoryRail>
        )}
        {!complete && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            busy={loadingMore}
            onClick={onLoadMore}
          >
            Older visits
          </Button>
        )}
      </div>
    </>
  );
};
